import type { InternalAxiosRequestConfig } from 'axios';

import {
  createClient,
  PeanudsApi,
  registerHttpSecurityHandlers,
  type PendingLivenessLaunchResult,
  type PendingLivenessLaunchStatus,
} from '@peanuds/shared';
import { reportAuthFlowBlocked } from '@peanuds/shared/observability/runtime-observability';

import { useAuthStore } from '../auth0/store/auth.store';
import { useAuthSessionStore } from '../auth0/store/auth-session.store';
import type {
  PendingLivenessDialogChallengeStatus,
  PendingLivenessDialogState,
} from '../components/pending-liveness-dialog';
import {
  clearPendingLivenessDialog,
  getPendingLivenessDialogState,
  registerPendingLivenessDialogOpenHandler,
  showPendingLivenessDialog,
} from '../components/pending-liveness-dialog';
import { appPinia } from '../plugins/pinia';
import { appConfig } from '../providers/config';
import { markPendingLivenessDuringAuthCallback } from '../auth0/auth-callback/pending-liveness-callback-state';
import { useUserContextStore } from '../store/user-context.store';
import { notifyPendingLivenessApproved } from './pending-liveness-approval';

const defaultHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
const PENDING_LIVENESS_STATUS_POLL_INTERVAL_MS = 5_000;

let pendingLivenessLaunchPromise: Promise<PendingLivenessLaunchResult> | null = null;
let activePendingLivenessLaunchPromise: Promise<PendingLivenessLaunchResult> | null = null;
let pendingLivenessPollTimer: number | null = null;
let trackedPendingLivenessChallengeId: string | null = null;
let pendingLivenessDialogRegistrationPromise: Promise<void> | null = null;
let mfaRedirectPromise: Promise<void> | null = null;
let logoutPromise: Promise<void> | null = null;

const getAuthSessionStore = () => useAuthSessionStore(appPinia);
const getAuthStore = () => useAuthStore(appPinia);
const getUserContextStore = () => useUserContextStore(appPinia);

/** @internal Test-only reset for module-level HTTP security singleton state. */
export const resetHttpSecurityHandlersStateForTests = () => {
  pendingLivenessLaunchPromise = null;
  activePendingLivenessLaunchPromise = null;
  trackedPendingLivenessChallengeId = null;
  pendingLivenessDialogRegistrationPromise = null;
  mfaRedirectPromise = null;
  logoutPromise = null;
  clearPendingLivenessPoll();
};

const livenessLauncherClient = createClient({
  baseURL: appConfig.VITE_API_URL,
  headers: defaultHeaders,
});

const livenessLauncherApi = new PeanudsApi({ client: livenessLauncherClient });

const showPendingLivenessDialogState = async (state: Partial<PendingLivenessDialogState>) => {
  showPendingLivenessDialog(state);
};

const clearPendingLivenessDialogState = async () => {
  clearPendingLivenessDialog();
};

const getPendingLivenessDialogSnapshot = async () => getPendingLivenessDialogState();

const triggerLogout = () => {
  if (!logoutPromise) {
    logoutPromise = Promise.resolve()
      .then(() => getAuthStore().logout())
      .finally(() => {
        logoutPromise = null;
      });
  }

  return logoutPromise;
};

const attachAuthenticationHeaders = async (config: InternalAxiosRequestConfig) => {
  const authSessionStore = getAuthSessionStore();

  if (authSessionStore.jwtToken && authSessionStore.idToken && !authSessionStore.isTokenExpired) {
    config.headers.Authorization = `Bearer ${authSessionStore.jwtToken}`;
    config.headers['X-Id-Token'] = `Bearer ${authSessionStore.idToken}`;
    return config;
  }

  void triggerLogout();
  throw new Error('Unable to establish an authenticated session for liveness handling.');
};

livenessLauncherClient.instance.interceptors.request.use(attachAuthenticationHeaders);

const openPendingLivenessWindow = (url: string): PendingLivenessLaunchStatus => {
  if (typeof window === 'undefined') {
    return 'unavailable';
  }

  const livenessWindow = window.open(url, '_blank');

  if (livenessWindow === null) {
    console.warn('[http] Browser blocked opening the hosted liveness challenge window.');
    return 'blocked';
  }

  return 'opened';
};

const clearPendingLivenessPoll = () => {
  if (pendingLivenessPollTimer === null) {
    return;
  }

  clearTimeout(pendingLivenessPollTimer);
  pendingLivenessPollTimer = null;
};

const clearPendingLivenessState = () => {
  activePendingLivenessLaunchPromise = null;
  trackedPendingLivenessChallengeId = null;
  clearPendingLivenessPoll();
  void clearPendingLivenessDialogState();
};

const clearActivePendingLivenessChallenge = () => {
  activePendingLivenessLaunchPromise = null;
  trackedPendingLivenessChallengeId = null;
  clearPendingLivenessPoll();
};

type ReusablePendingLivenessLaunchResult = PendingLivenessLaunchResult & {
  challengeId: string;
  url: string;
};

const isReusablePendingLivenessLaunch = (
  launchResult: PendingLivenessLaunchResult,
): launchResult is ReusablePendingLivenessLaunchResult => {
  return launchResult.challengeId !== null && launchResult.url !== null;
};

const schedulePendingLivenessStatusPoll = (challengeId: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  clearPendingLivenessPoll();
  pendingLivenessPollTimer = window.setTimeout(() => {
    void pollPendingLivenessChallengeStatus(challengeId);
  }, PENDING_LIVENESS_STATUS_POLL_INTERVAL_MS);
};

const pollPendingLivenessChallengeStatus = async (challengeId: string) => {
  if (trackedPendingLivenessChallengeId !== challengeId) {
    return;
  }

  try {
    const result = await livenessLauncherApi.getLivenessChallengeStatus({
      path: { id: challengeId },
      headers: { 'X-Id-Token': '' },
    });

    if (trackedPendingLivenessChallengeId !== challengeId) {
      return;
    }

    if (result instanceof Error) {
      schedulePendingLivenessStatusPoll(challengeId);
      return;
    }

    const challengeStatus =
      (result.data?.data?.status as PendingLivenessDialogChallengeStatus | undefined) ?? 'pending';

    if (challengeStatus === 'approved') {
      getUserContextStore().clearLoadError();

      const authSessionStore = getAuthSessionStore();
      const refreshedToken = await authSessionStore.refreshAuthClaims();

      if (refreshedToken === null) {
        console.warn(
          '[http] Hosted liveness challenge approved, but Auth0 silent refresh did not return a new access token.',
        );
      }

      if (trackedPendingLivenessChallengeId === challengeId) {
        notifyPendingLivenessApproved();
        clearPendingLivenessState();
      }

      return;
    }

    await ensurePendingLivenessDialogHandlerRegistered();
    await showPendingLivenessDialogState({ challengeId, challengeStatus });

    if (challengeStatus === 'pending') {
      schedulePendingLivenessStatusPoll(challengeId);
      return;
    }

    clearActivePendingLivenessChallenge();
  } catch (error) {
    console.error('[http] Failed to refresh hosted liveness challenge status.', error);
    schedulePendingLivenessStatusPoll(challengeId);
  }
};

const syncPendingLivenessDialog = (launchResultPromise: Promise<PendingLivenessLaunchResult>) => {
  void launchResultPromise.then((launchResult) => {
    const { challengeId, status, url } = launchResult;

    void ensurePendingLivenessDialogHandlerRegistered().then(() =>
      showPendingLivenessDialogState({
        challengeId,
        challengeStatus: challengeId ? 'pending' : null,
        launchStatus: status,
        url,
      }),
    );

    if (!isReusablePendingLivenessLaunch(launchResult)) {
      return;
    }

    trackedPendingLivenessChallengeId = launchResult.challengeId;
    schedulePendingLivenessStatusPoll(launchResult.challengeId);
  });
};

const launchPendingLivenessChallenge = () => {
  if (pendingLivenessLaunchPromise) {
    return pendingLivenessLaunchPromise;
  }

  pendingLivenessLaunchPromise = (async () => {
    try {
      const result = await livenessLauncherApi.startLivenessChallenge({
        body: { sdk: 'hosted_web' },
        headers: { 'X-Id-Token': '' },
      });

      if (result instanceof Error) {
        console.error('[http] Failed to start hosted liveness challenge.', result);

        return {
          status: 'unavailable',
          challengeId: null,
          url: null,
        } satisfies PendingLivenessLaunchResult;
      }

      const challengeId = result.data?.data?.challengeId ?? null;
      const url = result.data?.data?.url;

      if (typeof url !== 'string' || url.length === 0) {
        console.warn('[http] Hosted liveness challenge returned no usable URL.');

        return {
          status: 'unavailable',
          challengeId,
          url: null,
        } satisfies PendingLivenessLaunchResult;
      }

      return {
        status: openPendingLivenessWindow(url),
        challengeId,
        url,
      } satisfies PendingLivenessLaunchResult;
    } catch (error) {
      console.error('[http] Unexpected hosted liveness launcher failure.', error);

      return {
        status: 'unavailable',
        challengeId: null,
        url: null,
      } satisfies PendingLivenessLaunchResult;
    } finally {
      pendingLivenessLaunchPromise = null;
    }
  })();

  return pendingLivenessLaunchPromise;
};

const ensurePendingLivenessDialogHandlerRegistered = async () => {
  if (pendingLivenessDialogRegistrationPromise) {
    return pendingLivenessDialogRegistrationPromise;
  }

  pendingLivenessDialogRegistrationPromise = (async () => {
    registerPendingLivenessDialogOpenHandler(async () => {
      const currentDialogState = await getPendingLivenessDialogSnapshot();

      if (currentDialogState.url) {
        const retriedStatus = openPendingLivenessWindow(currentDialogState.url);

        await showPendingLivenessDialogState({
          launchStatus: retriedStatus,
          url: currentDialogState.url,
        });

        return;
      }

      const launchResult = await launchPendingLivenessChallenge();

      if (launchResult.url && launchResult.status === 'blocked') {
        const retriedStatus = openPendingLivenessWindow(launchResult.url);

        await showPendingLivenessDialogState({
          challengeId: launchResult.challengeId,
          challengeStatus: launchResult.challengeId ? 'pending' : null,
          launchStatus: retriedStatus,
          url: launchResult.url,
        });

        return;
      }

      syncPendingLivenessDialog(Promise.resolve(launchResult));
    });
  })();

  return pendingLivenessDialogRegistrationPromise;
};

const handlePendingLivenessChallenge = async () => {
  if (activePendingLivenessLaunchPromise) {
    return activePendingLivenessLaunchPromise;
  }

  activePendingLivenessLaunchPromise = (async () => {
    markPendingLivenessDuringAuthCallback();
    await ensurePendingLivenessDialogHandlerRegistered();

    await showPendingLivenessDialogState({
      challengeId: null,
      challengeStatus: null,
      launchStatus: 'loading',
      url: null,
    });

    const launchResult = await launchPendingLivenessChallenge();
    syncPendingLivenessDialog(Promise.resolve(launchResult));

    if (!isReusablePendingLivenessLaunch(launchResult)) {
      activePendingLivenessLaunchPromise = null;
    }

    return launchResult;
  })();

  return activePendingLivenessLaunchPromise;
};

const triggerMfaRedirect = () => {
  if (!mfaRedirectPromise) {
    mfaRedirectPromise = Promise.resolve()
      .then(() => getAuthStore().redirectToMfaLogin())
      .catch((error) => {
        reportAuthFlowBlocked({
          operation: 'mfa-redirect',
          failureKind: 'redirect-failed',
        });
        console.error('[http] Failed to trigger MFA reauthentication redirect.', error);
      })
      .finally(() => {
        mfaRedirectPromise = null;
      });
  }

  return mfaRedirectPromise;
};

export const useHttpSecurityHandlers = () => {
  registerHttpSecurityHandlers({
    handlePendingLivenessCheck: handlePendingLivenessChallenge,
    handleMfaRequired: triggerMfaRedirect,
  });
};
